import { create } from "zustand";
import { devtools } from "zustand/middleware";

// An action may return a (already translated) error message to keep the dialog
// open and surface the failure instead of closing silently. Returning nothing
// means success and closes the dialog. `void` is required so handlers that
// return nothing stay valid; `undefined` would reject their Promise<void>.
// biome-ignore lint/suspicious/noConfusingVoidType: void keeps void-returning handlers assignable
export type ConfirmResult = { error: string } | void;
type ConfirmAction = () => Promise<ConfirmResult> | ConfirmResult;

export type ConfirmOptions = {
  title: string;
  message: string;
  confirmLabel: string;
  onConfirm: ConfirmAction;
};

export type ConfirmState = {
  isOpen: boolean;
  loading: boolean;
  title: string;
  message: string;
  confirmLabel: string;
  error: string;
  onConfirm: ConfirmAction | null;
  open(options: ConfirmOptions): void;
  confirm(): Promise<void>;
  close(): void;
};

export const ConfirmStore = create<ConfirmState>()(
  devtools((set, getState) => ({
    isOpen: false,
    loading: false,
    title: "",
    message: "",
    confirmLabel: "",
    error: "",
    onConfirm: null,

    open: (options: ConfirmOptions) => {
      set({
        isOpen: true,
        loading: false,
        error: "",
        title: options.title,
        message: options.message,
        confirmLabel: options.confirmLabel,
        onConfirm: options.onConfirm,
      });
    },

    confirm: async () => {
      const { onConfirm } = getState();
      if (!onConfirm) {
        return;
      }

      set({ loading: true, error: "" });
      const result = await onConfirm();
      if (result?.error) {
        set({ loading: false, error: result.error });
        return;
      }
      set({ isOpen: false, loading: false, error: "", onConfirm: null });
    },

    close: () => {
      set({ isOpen: false, loading: false, error: "", onConfirm: null });
    },
  })),
);
